import { currentPeriodLabel } from './currentPeriodLabel';
/**
 * Cumulative return series for the /stock Charts widget.
 * Always emits a fixed 10-year FY window (fy_9 … fy) so the X-axis matches
 * other charts even when early years have no price. Returns are anchored at
 * the oldest available fiscal-year price; years before that are null.
 * Uses precomputed stockreturn_* / totalreturn_* when present, otherwise
 * derives from priceclose (+ dps for total return).
 */

export type CumulativeReturnPoint = {
  label: string;
  priceReturn: number | null;
  totalReturn: number | null;
};

export type HistoricalsLike = Record<string, unknown> & {
  period_end_date_fy?: string | null;
};

function fySuffix(offset: number): string {
  return offset === 0 ? '_fy' : `_fy_${offset}`;
}

function readNum(historicals: HistoricalsLike, key: string): number | null {
  const raw = historicals[key];
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function fiscalYearLabel(
  periodEndDateFy: string | null | undefined,
  offset: number,
): string {
  if (!periodEndDateFy) return `FY-${offset}`;
  const year = parseInt(periodEndDateFy.substring(0, 4), 10);
  if (Number.isNaN(year)) return `FY-${offset}`;
  return `'${String(year - offset).slice(-2)}`;
}

/** Oldest fiscal-year slot (fy_9 … fy) with a usable year-end price. */
export function getPriceHistoryStartOffset(historicals: HistoricalsLike): number | null {
  for (let i = 9; i >= 0; i--) {
    const price = readNum(historicals, `priceclose${fySuffix(i)}`);
    if (price != null && price > 0) return i;
  }
  return null;
}

function computeTriAtOffset(
  historicals: HistoricalsLike,
  baseIdx: number,
  basePrice: number,
  targetIdx: number,
): number | null {
  if (targetIdx > baseIdx) return null;
  if (targetIdx === baseIdx) return basePrice;

  let tri = basePrice;
  for (let j = baseIdx - 1; j >= targetIdx; j--) {
    const newerPrice = readNum(historicals, `priceclose${fySuffix(j)}`);
    const olderPrice = readNum(historicals, `priceclose${fySuffix(j + 1)}`);
    const dps = readNum(historicals, `dps${fySuffix(j)}`) ?? 0;
    if (newerPrice == null || olderPrice == null || olderPrice <= 0) return null;
    tri = tri * (newerPrice + dps) / olderPrice;
  }
  return tri;
}

function computeTotalReturnAtOffset(
  historicals: HistoricalsLike,
  baseIdx: number,
  basePrice: number,
  targetIdx: number,
): number | null {
  const targetPrice = readNum(historicals, `priceclose${fySuffix(targetIdx)}`);
  if (targetPrice == null) return null;

  // No dividends in range → total return equals price return (handles sparse FY rows).
  let hasDividends = false;
  for (let j = baseIdx; j >= targetIdx; j--) {
    const dps = readNum(historicals, `dps${fySuffix(j)}`);
    if (dps != null && dps > 0) {
      hasDividends = true;
      break;
    }
  }
  if (!hasDividends) {
    return round2((targetPrice / basePrice - 1) * 100);
  }

  const tri = computeTriAtOffset(historicals, baseIdx, basePrice, targetIdx);
  if (tri == null) return null;
  return round2((tri / basePrice - 1) * 100);
}

/** Split ratios we recognise (forward; reverse splits use the reciprocal). */
const SPLIT_RATIOS = [2, 3, 4, 5, 6, 8, 10, 15, 20, 25, 30, 40, 50, 100];

/**
 * Factor to put `priceclose_ltm` on the same per-share basis as the fiscal-year
 * history, or 1 when they already match.
 *
 * After a fresh split the LTM price is post-split while the FY prices and
 * `shares_ltm` are still pre-split until the next full data refresh (CRWD
 * 4:1: FY price $441, LTM $263, so the line "fell" 620% → 330%). Market cap
 * stays right, so marketcap / (price × shares) jumps by the split ratio
 * between the last FY and LTM. Comparing the two (not LTM alone) leaves ADRs
 * alone: their price is per ADR and shares are ordinary shares in every
 * period. Only clean split ratios are accepted, and the adjusted price must
 * stay within a 4× move of the last FY close, so data glitches are skipped.
 */
export function getUnadjustedSplitFactor(historicals: HistoricalsLike): number {
  const priceLtm = readNum(historicals, 'priceclose_ltm');
  const sharesLtm = readNum(historicals, 'shares_ltm');
  const mcapLtm = readNum(historicals, 'marketcap_ltm');
  const priceFy = readNum(historicals, 'priceclose_fy');
  const sharesFy = readNum(historicals, 'shares_fy');
  const mcapFy = readNum(historicals, 'marketcap_fy');
  if (!priceLtm || !sharesLtm || !mcapLtm || !priceFy || !sharesFy || !mcapFy) return 1;
  if ([priceLtm, sharesLtm, mcapLtm, priceFy, sharesFy, mcapFy].some((v) => v <= 0)) return 1;

  const impliedLtm = mcapLtm / (priceLtm * sharesLtm);
  const impliedFy = mcapFy / (priceFy * sharesFy);
  const implied = impliedLtm / impliedFy;
  if (implied > 0.55 && implied < 1.8) return 1;

  const candidates = SPLIT_RATIOS.flatMap((k) => [k, 1 / k]);
  const factor = candidates.reduce((best, k) =>
    Math.abs(implied / k - 1) < Math.abs(implied / best - 1) ? k : best,
  );
  if (Math.abs(implied / factor - 1) > 0.08) return 1;

  // The quoted price itself must have jumped the split's way (down after a
  // forward split, up after a reverse one); otherwise it's a market-cap glitch.
  const rawMove = priceLtm / priceFy;
  if (factor > 1 ? rawMove > 0.75 : rawMove < 1.33) return 1;

  const move = (priceLtm * factor) / priceFy;
  if (move < 0.25 || move > 4) return 1;
  return factor;
}

/** Fixed lookback used by Charts / X-Ray so axes align across metrics. */
const CUMULATIVE_RETURN_WINDOW = 9;

export function buildCumulativeReturnsData(
  historicals: HistoricalsLike,
): CumulativeReturnPoint[] {
  const baseIdx = getPriceHistoryStartOffset(historicals);
  if (baseIdx == null) return [];

  const basePrice = readNum(historicals, `priceclose${fySuffix(baseIdx)}`);
  if (basePrice == null || basePrice <= 0) return [];

  const fy = historicals.period_end_date_fy;
  const points: CumulativeReturnPoint[] = [];

  for (let i = CUMULATIVE_RETURN_WINDOW; i >= 0; i--) {
    const label = fiscalYearLabel(fy, i);

    // Years before the first price: keep the slot for axis alignment.
    if (i > baseIdx) {
      points.push({ label, priceReturn: null, totalReturn: null });
      continue;
    }

    const suffix = fySuffix(i);
    let priceReturn = readNum(historicals, `stockreturn${suffix}`);
    let totalReturn = readNum(historicals, `totalreturn${suffix}`);

    const price = readNum(historicals, `priceclose${suffix}`);
    if (priceReturn == null && price != null) {
      priceReturn = round2((price / basePrice - 1) * 100);
    }
    if (totalReturn == null) {
      totalReturn = computeTotalReturnAtOffset(historicals, baseIdx, basePrice, i);
    }

    points.push({
      label,
      priceReturn,
      totalReturn,
    });
  }

  const rawPriceLtm = readNum(historicals, 'priceclose_ltm');
  if (rawPriceLtm != null && rawPriceLtm > 0) {
    const splitFactor = getUnadjustedSplitFactor(historicals);
    const priceLtm = rawPriceLtm * splitFactor;
    // Precomputed LTM returns mix post-split price with pre-split history.
    let priceReturn = splitFactor === 1 ? readNum(historicals, 'stockreturn_ltm') : null;
    let totalReturn = splitFactor === 1 ? readNum(historicals, 'totalreturn_ltm') : null;

    if (priceReturn == null) {
      priceReturn = round2((priceLtm / basePrice - 1) * 100);
    }
    if (totalReturn == null) {
      const triFy = computeTriAtOffset(historicals, baseIdx, basePrice, 0);
      const priceFy = readNum(historicals, 'priceclose_fy');
      if (triFy != null && priceFy != null && priceFy > 0) {
        totalReturn = round2(((triFy * priceLtm) / priceFy / basePrice - 1) * 100);
      } else {
        let hasDividends = false;
        for (let j = baseIdx; j >= 0; j--) {
          const dps = readNum(historicals, `dps${fySuffix(j)}`);
          if (dps != null && dps > 0) { hasDividends = true; break; }
        }
        if (!hasDividends) {
          totalReturn = round2((priceLtm / basePrice - 1) * 100);
        }
      }
    }

    points.push({
      label: currentPeriodLabel(points.map((p) => p.label)),
      priceReturn,
      totalReturn,
    });
  }

  return points;
}
